/**
 * Archivo: VentanaModificarMenu.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define una ventana modal para modificar los detalles de un menú específico, incluyendo su mes,
 * tipo (actual o pasado) y archivo PDF. También permite eliminar el menú.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/MenusComedorServicios: Funciones para interactuar con el servicio de menús.
 * 
 * Propósito:
 * Proveer una interfaz para actualizar o eliminar menús, gestionando la carga de archivos e interacciones con la API.
 */

import React, { useState } from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import cruzNegra from '../Imagenes/Iconos/CruzNegra.png';
import { actualizarMenu, eliminarMenu } from '../Servicios/MenusComedorServicios';

const VentanaModificarMenu = ({ onClose, actualizarLista, menu }) => {
    const [archivoPDF, setArchivoPDF] = useState(null);
    const [nombreArchivoPDF, setNombreArchivoPDF] = useState(menu.pdf_menu || '');
    const [mensajeError, setMensajeError] = useState('');
    const [mensajeExito, setMensajeExito] = useState('');

    const [pdfVaciado, setPdfVaciado] = useState(false);

    const [nuevoMenu, setNuevoMenu] = useState({
        id_menu: menu.id_menu,
        mes_menu: menu.mes_menu,
        tipo_menu: menu.esActual_menu ? 'Actual' : 'Pasado'
    });

    const manejarArchivoPDF = (e) => {
        const file = e.target.files[0];
        const tiposValidos = ['application/pdf'];
        if (file && tiposValidos.includes(file.type)) {
            setArchivoPDF(file);
            setNombreArchivoPDF(file.name);
            setPdfVaciado(false);
        } else {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo válido (pdf).');
        }
    };

    const manejarActualizarMenu = async () => {
        if (!nuevoMenu.mes_menu) {
            setMensajeExito('');
            setMensajeError('Por favor, añade un mes para el menú.');
            return;
        } else if (!nuevoMenu.tipo_menu) {
            setMensajeExito('');
            setMensajeError('Por favor, indica si es un menú actual o no.');
            return;
        }

        try {
            const token = Cookies.get('token');
            const formData = new FormData();

            for (const key in nuevoMenu) {
                formData.append(key, nuevoMenu[key]);
            }

            if (pdfVaciado) {
                formData.append('esVacioPdf_menu', true);
            } else if (archivoPDF !== null) {
                formData.append('pdf_menu', archivoPDF);
                formData.append('esVacioPdf_menu', false);
            }

            const result = await actualizarMenu(token, formData);

            if (result.success) {
                setMensajeError('');
                setMensajeExito('Menú actualizado con éxito');
                actualizarLista();
            } else {
                setMensajeError(result.message);
            }
        } catch (error) {
            setMensajeExito('');
            setMensajeError('Error al actualizar menú');
        }
    };

    const manejarEliminarMenu = async () => {
        try {
            const token = Cookies.get('token');
            //console.log("Eliminando el menu con id:", menu.id_menu)
            const result = await eliminarMenu(token, menu.id_menu);
            if (result.success) {
                actualizarLista();
                onClose();
            } else {
                setMensajeError(result.message);
            }
        } catch (error) {
            setMensajeError('Error al eliminar menú');
        }
    };

    const handleChangeCampo = (campo, valor) => {
        setNuevoMenu(prevMenu => ({
            ...prevMenu,
            [campo]: valor
        }));
    };

    return (
        <div className="container col-xl-6 col-lg-7 col-md-8 bdr-1 rounded p-xl-2 p-lg-1 p-md-0 position-fixed" style={{ fontWeight: '3rem', background: '#D9D9D9', top: '44%', left: '50%', transform: 'translate(-50%, -40%)', zIndex: '5' }}>
            <div className="row-fluid pb-0 d-flex">
                <div className="container col-12 pb-1 text-start" style={{ borderBottom: '2px solid white' }}>
                    <span className="customFontSizeTitulos text-black"> Modificar menú </span>
                    <img
                        src={cruzNegra}
                        alt="close-icon"
                        className="card-img-top"
                        style={{ position: 'absolute', height: '3rem', width: '3rem', cursor: 'pointer', top: 0, right: 0 }}
                        onClick={onClose}
                    />
                </div>
            </div>
            <div className='container-fluid pb-1 pt-1'>
                <form onSubmit={(e) => { e.preventDefault(); manejarActualizarMenu(); }}>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Mes del menú:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={nuevoMenu.mes_menu}
                            onChange={(e) => handleChangeCampo('mes_menu', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Menú actual o pasado:</label>
                        <select
                            className="form-control"
                            value={nuevoMenu.tipo_menu}
                            onChange={(e) => handleChangeCampo('tipo_menu', e.target.value)}
                        >
                            <option value="Actual">Actual</option>
                            <option value="Pasado">Pasado</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>PDF del menú:</label>
                        <div className="d-flex align-items-center">
                            <input
                                type="file"
                                id="pdf_input"
                                style={{ display: 'none' }}
                                accept=".pdf"
                                onChange={manejarArchivoPDF}
                            />
                            <button 
                                type="button" 
                                className="btn btn-link"
                                onClick={() => document.getElementById('pdf_input').click()}
                            >
                                Seleccionar archivo
                            </button>
                            <input
                                type="text"
                                className="form-control ml-2"
                                value={nombreArchivoPDF}
                                readOnly
                                style={{ cursor: 'pointer' }}
                            />
                            <button 
                                type="button" 
                                className="btn btn-link text-danger ml-2" 
                                onClick={() => {
                                    setNombreArchivoPDF('');
                                    setArchivoPDF(null);
                                    setPdfVaciado(true);
                                }}>
                                Vaciar
                            </button>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <button type="submit" className='btn text-white' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                            Confirmar
                        </button>
                        <button type="button" className='btn text-danger' style={{ cursor: 'pointer', padding: '5px 10px' }} onClick={manejarEliminarMenu}>
                            Eliminar menú
                        </button>
                    </div>
                </form>
                {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
            </div>
        </div>
    );
};

export default VentanaModificarMenu;
